import React, { useState } from 'react';
import {
  RotateCw,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Copy,
  Eye,
  GripVertical,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { StudioPage } from '../types';

interface PageThumbnailCardProps {
  page: StudioPage;
  index: number;
  totalCount: number;
  isSelected: boolean;
  onToggleSelect: (pageId: string) => void;
  onRotateCw: (pageId: string) => void;
  onRotateCcw: (pageId: string) => void;
  onMoveLeft: (index: number) => void;
  onMoveRight: (index: number) => void;
  onMoveToFirst?: (index: number) => void;
  onMoveToLast?: (index: number) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  onDuplicate: (pageId: string) => void;
  onDelete: (pageId: string) => void;
  onPreview: (page: StudioPage) => void;
}

export const PageThumbnailCard: React.FC<PageThumbnailCardProps> = ({
  page,
  index,
  totalCount,
  isSelected,
  onToggleSelect,
  onRotateCw,
  onRotateCcw,
  onMoveLeft,
  onMoveRight,
  onMoveToFirst,
  onMoveToLast,
  onReorder,
  onDuplicate,
  onDelete,
  onPreview,
}) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEditingPosition, setIsEditingPosition] = useState(false);
  const [targetPosition, setTargetPosition] = useState(String(index + 1));

  const handlePositionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditingPosition(false);
    const parsed = parseInt(targetPosition, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= totalCount && onReorder) {
      onReorder(index, parsed - 1);
    } else {
      setTargetPosition(String(index + 1));
    }
  };

  return (
    <div
      draggable={!!onReorder}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (onReorder) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setIsDragOver(true);
        }
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const fromIndexStr = e.dataTransfer.getData('text/plain');
        const fromIdx = parseInt(fromIndexStr, 10);
        if (!isNaN(fromIdx) && fromIdx !== index && onReorder) {
          onReorder(fromIdx, index);
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-surface)',
        border: isDragOver
          ? '2px dashed var(--primary-600)'
          : isSelected
          ? '2px solid var(--primary-500)'
          : '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: isDragOver
          ? '0 8px 24px rgba(37, 99, 235, 0.25)'
          : isSelected
          ? '0 0 0 3px rgba(37, 99, 235, 0.15)'
          : 'var(--shadow-sm)',
        overflow: 'hidden',
        transition: 'all 0.15s ease',
        position: 'relative',
        transform: isDragOver ? 'scale(1.02)' : 'none',
      }}
    >
      {/* Top Header Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-2) var(--space-3)',
          backgroundColor: isSelected ? 'var(--primary-50)' : 'var(--bg-surface-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
          gap: 'var(--space-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {onReorder && (
            <div
              title="Drag to reorder page"
              style={{ cursor: 'grab', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center' }}
            >
              <GripVertical size={14} />
            </div>
          )}

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(page.id)}
              style={{ cursor: 'pointer', accentColor: 'var(--primary-600)' }}
            />
            {isEditingPosition ? (
              <form onSubmit={handlePositionSubmit} style={{ display: 'inline-flex' }}>
                <input
                  type="number"
                  min={1}
                  max={totalCount}
                  value={targetPosition}
                  onChange={(e) => setTargetPosition(e.target.value)}
                  onBlur={handlePositionSubmit}
                  autoFocus
                  style={{
                    width: '36px',
                    height: '20px',
                    fontSize: '11px',
                    padding: '0 2px',
                    textAlign: 'center',
                    border: '1px solid var(--primary-600)',
                    borderRadius: 'var(--radius-xs)',
                    backgroundColor: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                  }}
                />
              </form>
            ) : (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingPosition(true);
                  setTargetPosition(String(index + 1));
                }}
                title="Click to jump to page number"
                style={{
                  padding: '1px 5px',
                  borderRadius: 'var(--radius-xs)',
                  backgroundColor: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                }}
              >
                #{index + 1}
              </span>
            )}
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', overflow: 'hidden' }}>
          {page.rotation !== 0 && (
            <span
              style={{
                fontSize: '10px',
                padding: '1px 5px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--color-warning)',
                color: '#fff',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {page.rotation}°
            </span>
          )}
          <span
            style={{
              fontSize: '10px',
              padding: '1px 6px',
              borderRadius: 'var(--radius-sm)',
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-subtle)',
              maxWidth: '90px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={page.sourceDocName}
          >
            {page.sourceDocName}
          </span>
        </div>
      </div>

      {/* Thumbnail View Area */}
      <div
        style={{
          height: '240px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-surface-subtle)',
          padding: 'var(--space-4)',
          position: 'relative',
          cursor: 'pointer',
        }}
        onClick={() => onPreview(page)}
      >
        <div
          style={{
            width: '150px',
            height: '210px',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
            borderRadius: '2px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            transform: `rotate(${page.rotation}deg)`,
            transition: 'transform 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {page.thumbnailDataUrl ? (
            <img
              src={page.thumbnailDataUrl}
              alt={`Page ${index + 1}`}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              loading="lazy"
            />
          ) : (
            <div style={{ color: '#64748b', fontSize: 'var(--font-size-xs)' }}>Page {index + 1}</div>
          )}
        </div>

        {/* Hover preview button */}
        <div
          style={{
            position: 'absolute',
            bottom: 'var(--space-2)',
            right: 'var(--space-2)',
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            color: '#ffffff',
            borderRadius: 'var(--radius-sm)',
            padding: '2px 6px',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '10px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Eye size={11} /> Zoom
        </div>
      </div>

      {/* Control Action Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-2) var(--space-3)',
          borderTop: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        {/* Reordering Controls */}
        <div style={{ display: 'flex', gap: '2px' }}>
          {onMoveToFirst && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '4px 4px', height: '26px' }}
              disabled={index === 0}
              onClick={() => onMoveToFirst(index)}
              title="Move to Front (#1)"
              aria-label="Move to Front"
            >
              <ChevronsLeft size={13} />
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '4px 4px', height: '26px' }}
            disabled={index === 0}
            onClick={() => onMoveLeft(index)}
            title="Move Page Earlier (Left/Up)"
            aria-label="Move Page Earlier"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '4px 4px', height: '26px' }}
            disabled={index === totalCount - 1}
            onClick={() => onMoveRight(index)}
            title="Move Page Later (Right/Down)"
            aria-label="Move Page Later"
          >
            <ChevronRight size={14} />
          </button>
          {onMoveToLast && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '4px 4px', height: '26px' }}
              disabled={index === totalCount - 1}
              onClick={() => onMoveToLast(index)}
              title="Move to End"
              aria-label="Move to End"
            >
              <ChevronsRight size={13} />
            </button>
          )}
        </div>

        {/* Rotation Controls */}
        <div style={{ display: 'flex', gap: '2px' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '4px 4px', height: '26px' }}
            onClick={() => onRotateCcw(page.id)}
            title="Rotate 90° Counter-Clockwise"
            aria-label="Rotate 90° Counter-Clockwise"
          >
            <RotateCcw size={13} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '4px 4px', height: '26px' }}
            onClick={() => onRotateCw(page.id)}
            title="Rotate 90° Clockwise"
            aria-label="Rotate 90° Clockwise"
          >
            <RotateCw size={13} />
          </button>
        </div>

        {/* Duplicate & Delete */}
        <div style={{ display: 'flex', gap: '2px' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '4px 4px', height: '26px' }}
            onClick={() => onDuplicate(page.id)}
            title="Duplicate Page"
            aria-label="Duplicate Page"
          >
            <Copy size={13} />
          </button>

          {!showConfirmDelete ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '4px 4px', height: '26px', color: 'var(--color-danger)' }}
              onClick={() => setShowConfirmDelete(true)}
              title="Delete Page"
              aria-label="Delete Page"
            >
              <Trash2 size={13} />
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{
                padding: '2px 6px',
                height: '26px',
                backgroundColor: 'var(--color-danger)',
                borderColor: 'var(--color-danger)',
                fontSize: '10px',
                fontWeight: 700,
              }}
              onClick={() => onDelete(page.id)}
              onBlur={() => setShowConfirmDelete(false)}
              title="Confirm Delete"
            >
              Del?
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
